import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Inbox } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import TaskFilters from './TaskFilters';
import TaskCounters, { computeCounters } from './TaskCounters';
import TaskRow from './TaskRow';
import TaskDetailDialog from './TaskDetailDialog';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';
import { API } from '../../utils/kitchenTasks';
import { formatApiError } from '../../services/api';

const TaskTrackingPanel = ({ date, onDateChange, collaborators, refreshKey }) => {
  const [filters, setFilters] = useState({ date, service: '', category: '', assigned_user_id: '', status: '', priority: '', archived: false, quick: '' });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setFilters(f => (f.date === date ? f : { ...f, date })); }, [date]);

  const fetchTasks = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { date: filters.date, archived: filters.archived };
      if (filters.service) params.service = filters.service;
      if (filters.category) params.category = filters.category;
      if (filters.assigned_user_id) params.assigned_user_id = filters.assigned_user_id;
      if (filters.priority) params.priority = filters.priority;
      const res = await axios.get(`${API}/kitchen-tasks`, { params });
      setTasks(res.data.data || []);
    } catch (err) { if (!silent) toast.error(formatApiError(err)); } finally { setLoading(false); }
  }, [filters.date, filters.service, filters.category, filters.assigned_user_id, filters.priority, filters.archived]);

  useEffect(() => { fetchTasks(); }, [fetchTasks, refreshKey]);
  useLiveRefresh(() => fetchTasks(true));

  const counters = useMemo(() => computeCounters(tasks), [tasks]);
  const visible = useMemo(() => tasks.filter(t => {
    if (filters.status && t.status !== filters.status) return false;
    if (filters.quick === 'late' && !t.is_late) return false;
    if (filters.quick === 'terminee' && !['terminee', 'validee', 'a_valider'].includes(t.status)) return false;
    if (filters.quick === 'unassigned' && ((t.assigned_user_ids || []).length || ['validee', 'annulee'].includes(t.status))) return false;
    return true;
  }), [tasks, filters.status, filters.quick]);

  const handleQuick = (key, f) => {
    if (filters.quick === key) { setFilters({ ...filters, quick: '', status: '' }); return; }
    setFilters({ ...filters, quick: key, status: f?.status || '' });
  };

  const handleChanged = (updated) => {
    setTasks(prev => prev.map(t => (t.id === updated.id ? updated : t)));
    setSelected(updated.archived && !filters.archived ? null : updated);
    fetchTasks(true);
  };

  const handleFilters = (f) => {
    if (f.date !== filters.date) onDateChange?.(f.date);
    setFilters(f);
  };

  return (
    <div className="space-y-4" data-testid="task-tracking-panel">
      <TaskFilters filters={filters} onChange={handleFilters} collaborators={collaborators} onRefresh={() => fetchTasks()} loading={loading} />
      <TaskCounters counters={counters} onQuickFilter={handleQuick} active={filters.quick} />
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-10 h-10 text-neon-pink animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-10 text-center" data-testid="tracking-empty">
          <Inbox className="mx-auto text-gray-300 mb-2" size={36} />
          <p className="text-gray-600 font-medium">Aucune tâche pour ces critères.</p>
          <p className="text-gray-400 text-sm">Sélectionnez des mises en place dans l'onglet « Liste » pour créer des tâches.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="tracking-list">
          {visible.map(t => <TaskRow key={t.id} task={t} onOpen={setSelected} />)}
        </div>
      )}
      <TaskDetailDialog task={selected} open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }} collaborators={collaborators} onChanged={handleChanged} />
    </div>
  );
};

export default TaskTrackingPanel;
